import { create, StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';
import {
  CompanyProperties,
  initialCompany,
} from '../screens/company/types/company.type';
import {
  GroupProperties,
  initialGroup,
} from '../screens/group/types/group.type';
import {
  AssetProperties,
  initialAsset,
} from '../screens/asset/types/asset.type';
import {
  ELogFilterRangeType,
  EReportDataInterval,
  ILogReportFilter,
} from '../screens/report/types/report-log.type';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  DeviceProperties,
  initialDevice,
} from '../screens/device/types/device.type';

dayjs.extend(utc);

type CompanyProps = {
  group: GroupProperties;
  company: CompanyProperties;
  asset: AssetProperties;
  devices: DeviceProperties[];
  feature: string;
  dataSource: 'GSM' | 'SATELLITE';
  filter: ILogReportFilter;
  timezone: number;
  browserTimezone: number;
  manualTimezone: number | null;
  setGroup: (group: GroupProperties) => void;
  setCompany: (company: CompanyProperties) => void;
  setAsset: (asset: AssetProperties) => void;
  setDevices: (devices: DeviceProperties[]) => void;
  setFeature: (value: string) => void;
  setDataSource: (value: 'GSM' | 'SATELLITE') => void;
  setFilter: (value: ILogReportFilter) => void;
  setTimezone: (timezone: number) => void;
  syncBrowserTimezone: (timezone: number) => void;
};

const getBrowserTimezoneOffset = (): number => {
  const offset = new Date().getTimezoneOffset() / -60;
  return offset;
};

const browserTzOffset = getBrowserTimezoneOffset();

export const initialLogReportQuery: ILogReportFilter = {
  deviceType: '',
  assetId: '',
  interval: EReportDataInterval.minute,
  startAt: dayjs().utcOffset(browserTzOffset * 60).startOf('date').unix(),
  endAt: dayjs().utcOffset(browserTzOffset * 60).endOf('date').unix(),
  // startAt: 1732813200,
  // endAt: 1732820400,
  timezone: browserTzOffset,
};

type MyPersist = (
  config: any,
  options: PersistOptions<CompanyProps>
) => StateCreator<CompanyProps>;

const useGroup = create<CompanyProps>(
  (persist as MyPersist)(
    (set: any, get: any) => ({
      group: initialGroup,
      company: initialCompany,
      asset: initialAsset,
      devices: [],
      feature: '',
      filter: { ...initialLogReportQuery },
      dataSource: 'GSM',
      timezone: browserTzOffset,
      browserTimezone: browserTzOffset,
      manualTimezone: null,
      setGroup: (group: GroupProperties) => {
        set({ group });
      },
      setCompany: (company: CompanyProperties) => {
        set({ company });
      },
      setAsset: (asset: AssetProperties) => {
        set({ asset });
        set((state: any) => ({
          filter: {
            ...state.filter,
            assetId: asset.assetId,
          },
        }));
      },
      setFilter: (filter: ILogReportFilter) => {
        set({ filter });
      },
      setFeature: (value: string) => {
        set({ feature: value });
      },
      setDevices: (devices: DeviceProperties[]) => {
        set({ devices });
      },
      setDataSource: (value: 'GSM' | 'SATELLITE') => {
        set({ dataSource: value });
      },
      setTimezone: (timezone: number) => {
        set({ manualTimezone: timezone, timezone });
        set((state: any) => {
          const newFilter = {
            ...state.filter,
            timezone,
          };
          return { filter: newFilter };
        });
      },
      syncBrowserTimezone: (timezone: number) => {
        set((state: any) => {
          const effectiveTimezone = state.manualTimezone ?? timezone;
          return {
            browserTimezone: timezone,
            timezone: effectiveTimezone,
            filter: {
              ...state.filter,
              timezone: effectiveTimezone,
            },
          };
        });
      },
    }),
    {
      name: 'group-selected',
      getStorage: () => localStorage,
      partialize: (state) => ({
        group: state.group,
        company: state.company,
        asset: state.asset,
        devices: state.devices,
        feature: state.feature,
        dataSource: state.dataSource,
        manualTimezone: state.manualTimezone,
        filter: {
          ...state.filter,
          timezone: state.timezone,
        },
      }) as any,
      onRehydrateStorage: () => (state) => {
        if (state) {
          const currentBrowserTz = getBrowserTimezoneOffset();
          const manualTimezone = Number.isFinite(state.manualTimezone)
            ? state.manualTimezone
            : null;
          const timezone = manualTimezone ?? currentBrowserTz;

          state.browserTimezone = currentBrowserTz;
          state.manualTimezone = manualTimezone;
          state.timezone = timezone;
          state.filter = {
            ...state.filter,
            timezone,
          };
        }
      },
    }
  )
);

export default useGroup;
